import { createFileRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import api from '@/api';
import { ArchiveSidebar } from '@/archive/ArchiveSidebar';
import { nextArchivePage, PAGE_SIZE, PostArchive } from '@/archive/PostArchive';
import { apiLanguage } from '~/i18n';
import { localeToApiLanguage } from '~/utils/languageFormat';

/**
 * `/news` is the student news archive. Study helper news lives at
 * `/study-helper-news`, as legacy names it.
 *
 * No search control; see `ArchiveSidebar`.
 */
function StudentNewsArchive(): ReactElement {
  // The interface language, passed as a parameter so it's part of the query key
  // and a language switch refetches; the `x-wbs-language` header isn't in any
  // query key. Unlike legacy there's no language prefix in the URL.
  const language = localeToApiLanguage(apiLanguage());

  const query = api.listStudentNews.useInfiniteQuery(
    { query: { language, limit: PAGE_SIZE } },
    {
      initialPageParam: { query: { offset: 0 } },
      // Wrapped rather than passed by reference: an annotated `lastPage`
      // would be inferred as the operation's `TQueryFnData` and collapse the
      // page type to the helper's structural one.
      getNextPageParam: (lastPage, allPages) =>
        nextArchivePage(lastPage, allPages),
    },
  );

  return (
    <PostArchive
      query={query}
      basePath="/news"
      emptyTestId="student-news-empty"
      sidebar={
        <ArchiveSidebar
          sidebar="student-news-sidebar"
          language={language}
          categoryTo="/news/category/$slug"
        />
      }
    />
  );
}

export const Route = createFileRoute('/_app/news/')({
  component: StudentNewsArchive,
});
